import type { SVGProps } from "react";

export type ArtistIconName =
	| "back"
	| "check"
	| "close"
	| "heart"
	| "message"
	| "trash"
	| "download"
	| "more"
	| "play"
	| "share"
	| "home"
	| "music"
	| "create"
	| "generate"
	| "person"
	| "search"
	| "camera"
	| "send"
	| "waveform"
	| "pause"
	| "sparkle"
	| "plus"
	| "image"
	| "mail"
	| "lock"
	| "phone"
	| "link"
	| "crown"
	| "shield"
	| "chevron"
	| "globe"
	| "info"
	| "doc"
	| "sliders"
	| "circle"
	| "card"
	| "gift";

export interface ArtistIconProps extends SVGProps<SVGSVGElement> {
	readonly name: ArtistIconName;
	readonly size?: number;
	readonly strokeWidth?: number;
	readonly className?: string;
}

export function ArtistIcon({
	name,
	size = 20,
	strokeWidth = 1.8,
	className = "",
	...props
}: Readonly<ArtistIconProps>) {
	const common = {
		fill: "none",
		stroke: "currentColor",
		strokeLinecap: "round" as const,
		strokeLinejoin: "round" as const,
		strokeWidth,
	};
	return (
		<svg
			className={`ds-icon ${className}`.trim()}
			width={size}
			height={size}
			viewBox="0 0 24 24"
			aria-hidden="true"
			focusable="false"
			{...props}
		>
			{name === "back" ? <path {...common} d="m15 18-6-6 6-6" /> : null}
			{name === "check" ? <path {...common} d="m5 12 4 4L19 6" /> : null}
			{name === "circle" ? <circle {...common} cx="12" cy="12" r="9" /> : null}
			{name === "card" ? (
				<>
					<rect {...common} x="3" y="5" width="18" height="14" rx="2" />
					<path {...common} d="M3 10h18" />
				</>
			) : null}
			{name === "gift" ? (
				<>
					<rect {...common} x="3" y="9" width="18" height="12" rx="2" />
					<path {...common} d="M12 9v12M3 13h18M7.5 9C5 9 4 7.8 4 6.5S5 4 6.5 4C9 4 12 9 12 9s3-5 5.5-5C19 4 20 5.2 20 6.5S19 9 16.5 9" />
				</>
			) : null}
			{name === "close" ? (
				<>
					<path {...common} d="m6 6 12 12" />
					<path {...common} d="M18 6 6 18" />
				</>
			) : null}
			{name === "heart" ? (
				<path
					{...common}
					d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z"
				/>
			) : null}
			{name === "message" ? (
				<path
					{...common}
					d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z"
				/>
			) : null}
			{name === "trash" ? (
				<path {...common} d="M4 7h16M9 7V4h6v3m3 0-1 14H7L6 7m4 4v6m4-6v6" />
			) : null}
			{name === "download" ? (
				<>
					<path {...common} d="M12 3v12m-4-4 4 4 4-4" />
					<path {...common} d="M5 19h14" />
				</>
			) : null}
			{name === "more" ? (
				<>
					<circle cx="5" cy="12" r="1.4" fill="currentColor" />
					<circle cx="12" cy="12" r="1.4" fill="currentColor" />
					<circle cx="19" cy="12" r="1.4" fill="currentColor" />
				</>
			) : null}
			{name === "play" ? (
				<path d="m8 5 11 7-11 7Z" fill="currentColor" />
			) : null}
			{name === "share" ? (
				<>
					<path {...common} d="m14 5 4-3 4 3" />
					<path {...common} d="M18 2v12" />
					<path {...common} d="M16 8H7a4 4 0 0 0-4 4v8h18v-8" />
				</>
			) : null}
			{name === "home" ? (
				<>
					<path {...common} d="m3 11 9-8 9 8" />
					<path {...common} d="M5 10v11h14V10M9 21v-7h6v7" />
					<path {...common} d="M8 8h3" />
				</>
			) : null}
			{name === "music" ? (
				<>
					<path {...common} d="M9 18V5l10-2v13" />
					<circle cx="6" cy="18" r="3" fill="currentColor" />
					<circle cx="16" cy="16" r="3" fill="currentColor" />
				</>
			) : null}
			{name === "create" ? (
				<>
					<rect {...common} x="3" y="3" width="18" height="18" rx="3" />
					<path {...common} d="M12 7v10M7 12h10" />
				</>
			) : null}
			{name === "generate" ? (
				<path {...common} d="M3 5h3v14H3zM9 5h3v14H9zM15 5h6v14h-6z" />
			) : null}
			{name === "person" ? (
				<>
					<circle {...common} cx="12" cy="8" r="4" />
					<path {...common} d="M4 21a8 8 0 0 1 16 0" />
				</>
			) : null}
			{name === "search" ? (
				<>
					<circle {...common} cx="10.5" cy="10.5" r="7" />
					<path {...common} d="m16 16 5 5" />
				</>
			) : null}
			{name === "camera" ? (
				<>
					<path {...common} d="M4 7h4l2-2h4l2 2h4v12H4z" />
					<circle {...common} cx="12" cy="13" r="4" />
				</>
			) : null}
			{name === "send" ? (
				<>
					<path {...common} d="m4 12 16-8-5 16-3-6Z" />
					<path {...common} d="m12 14 8-10" />
				</>
			) : null}
			{name === "waveform" ? (
				<path {...common} d="M4 10v4m4-8v12m4-15v18m4-15v12m4-8v4" />
			) : null}
			{name === "pause" ? <path {...common} d="M8 5v14M16 5v14" /> : null}
			{name === "sparkle" ? (
				<>
					<path
						{...common}
						d="m12 2 1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5Z"
					/>
					<path
						{...common}
						d="m19 15 .7 2.3L22 18l-2.3.7L19 21l-.7-2.3L16 18l2.3-.7Z"
					/>
				</>
			) : null}
			{name === "plus" ? <path {...common} d="M12 5v14M5 12h14" /> : null}
			{name === "image" ? (
				<>
					<rect {...common} x="3" y="4" width="18" height="16" rx="2" />
					<circle {...common} cx="9" cy="10" r="2" />
					<path {...common} d="m5 18 5-5 3 3 2-2 4 4" />
				</>
			) : null}
			{name === "mail" ? (
				<>
					<rect {...common} x="3" y="5" width="18" height="14" rx="2" />
					<path {...common} d="m4 7 8 6 8-6" />
				</>
			) : null}
			{name === "lock" ? (
				<>
					<rect {...common} x="5" y="10" width="14" height="11" rx="2" />
					<path {...common} d="M8 10V7a4 4 0 0 1 8 0v3" />
				</>
			) : null}
			{name === "phone" ? (
				<path
					{...common}
					d="M7 3h3l1 5-2 1a15 15 0 0 0 6 6l1-2 5 1v3a4 4 0 0 1-4 4C9.3 20.2 3.8 14.7 3 7a4 4 0 0 1 4-4Z"
				/>
			) : null}
			{name === "link" ? (
				<>
					<path
						{...common}
						d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1.2 1.2"
					/>
					<path
						{...common}
						d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1.2-1.2"
					/>
				</>
			) : null}
			{name === "crown" ? (
				<>
					<path {...common} d="m3 7 5 4 4-7 4 7 5-4-2 12H5Z" />
					<path {...common} d="M6 16h12" />
				</>
			) : null}
			{name === "shield" ? (
				<>
					<path {...common} d="M12 3 4 6v6c0 5 3.4 8 8 9 4.6-1 8-4 8-9V6Z" />
					<path {...common} d="m8.5 12 2.2 2.2 4.8-5" />
				</>
			) : null}
			{name === "globe" ? (
				<>
					<circle {...common} cx="12" cy="12" r="9" />
					<path {...common} d="M3 12h18" />
					<path
						{...common}
						d="M12 3c2.5 3 3.5 6 3.5 9s-1 6-3.5 9c-2.5-3-3.5-6-3.5-9s1-6 3.5-9z"
					/>
				</>
			) : null}
			{name === "info" ? (
				<>
					<circle {...common} cx="12" cy="12" r="9" />
					<path {...common} d="M12 11v5" />
					<circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
				</>
			) : null}
			{name === "doc" ? (
				<>
					<path
						{...common}
						d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
					/>
					<path {...common} d="M14 3v5h5" />
					<path {...common} d="M9 13h6M9 17h6" />
				</>
			) : null}
			{name === "sliders" ? (
				<>
					<path {...common} d="M4 6h16" />
					<circle cx="10" cy="6" r="2" fill="currentColor" stroke="none" />
					<path {...common} d="M4 12h16" />
					<circle cx="15" cy="12" r="2" fill="currentColor" stroke="none" />
					<path {...common} d="M4 18h16" />
					<circle cx="8" cy="18" r="2" fill="currentColor" stroke="none" />
				</>
			) : null}
			{name === "chevron" ? <path {...common} d="m9 6 6 6-6 6" /> : null}
		</svg>
	);
}
